import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export type TimeBucket = 'day' | 'week' | 'month';
export type RangeKey = '24h' | '7d' | 'month' | 'year' | 'all';

type OrdersSummary = {
  status: 'success' | 'failed';
  totalOrders: number;
  totalRevenue: string;
  time: TimeBucket;
  timezone: string;
};

// BE can return EITHER an array of {status,count} OR an object map { status: count }
type OrderStatusCountsArrayItem = { status: string; count: number };
type OrderStatusCounts = Record<string, number> | OrderStatusCountsArrayItem[];

type OrderStatusCountBlock = {
  status: 'success' | 'failed';
  orderStatusCounts: OrderStatusCounts;
  timezone: string;
  range: { start: string; end: string };
};

type ApiResponse = {
  status: 'success' | 'failed';
  message?: string;
  data?: {
    totalOrdersToday?: OrdersSummary;
    totalOrdersThisWeek?: OrdersSummary;
    totalOrdersThisMonth?: OrdersSummary;

    // v2 blocks (as per your backend example)
    orderStatusCount24?: OrderStatusCountBlock;
    orderStatusCount7?: OrderStatusCountBlock;
    orderStatusCountMonth?: OrderStatusCountBlock;
    orderStatusCountYear?: OrderStatusCountBlock;
    orderStatusCountAll?: OrderStatusCountBlock;

    // legacy fallback (your old API)
    orderStatusCount?: OrderStatusCountBlock;
  };
};

export type StatusKind =
  | 'pending'
  | 'confirmed'
  | 'cooking'
  | 'delivery'
  | 'completed'
  | 'cancelled'
  | 'other';

export type StatusRow = {
  key: string;
  label: string;
  count: number;
  percent: number;
  kind: StatusKind;
};

export type StatusPanel = {
  rangeLabel: string;
  rangeStart: string;
  rangeEnd: string;
  total: number;
  rows: StatusRow[];
};

export interface AdminDashboardV2VM {
  timezone: string;
  totals: {
    today: { totalOrders: number; totalRevenue: string };
    week: { totalOrders: number; totalRevenue: string };
    month: { totalOrders: number; totalRevenue: string };
  };
  statusPanels: Record<RangeKey, StatusPanel>;
  attention: {
    pending: number;
    inProgress: number;
    cancelled24h: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  constructor(private http: HttpClient) {}

  getDashboardData(): Observable<AdminDashboardV2VM> {
    const params = new HttpParams().set('action', 'dashboardData');

    return this.http
      .get<ApiResponse>(environment.orderUrl, { withCredentials: true, params })
      .pipe(
        map((res) => {
          const d = res?.data ?? {};

          // ---------- totals ----------
          const safeTotals = (x?: OrdersSummary | null) => ({
            totalOrders: x?.totalOrders ?? 0,
            totalRevenue: this.money(x?.totalRevenue ?? '0.00'),
          });

          const totals = {
            today: safeTotals(d.totalOrdersToday),
            week: safeTotals(d.totalOrdersThisWeek),
            month: safeTotals(d.totalOrdersThisMonth),
          };

          const timezone =
            d.totalOrdersToday?.timezone ??
            d.orderStatusCount24?.timezone ??
            d.orderStatusCount?.timezone ??
            'UTC';

          // ---------- status panels ----------
          // If v2 exists use it, else fall back to legacy `orderStatusCount` into 24h
          const blocks = {
            '24h': d.orderStatusCount24 ?? d.orderStatusCount,
            '7d': d.orderStatusCount7,
            month: d.orderStatusCountMonth,
            year: d.orderStatusCountYear,
            all: d.orderStatusCountAll,
          };

          const statusPanels: Record<RangeKey, StatusPanel> = {
            '24h': this.mapStatusPanel('Last 24 hours', blocks['24h']),
            '7d': this.mapStatusPanel('Last 7 days', blocks['7d']),
            month: this.mapStatusPanel('This month', blocks['month']),
            year: this.mapStatusPanel('This year', blocks['year']),
            all: this.mapStatusPanel('All time', blocks['all']),
          };

          // ---------- attention ----------
          // Best current indicator = 24h pending/cancelled
          const c24 = this.normalizeCounts(blocks['24h']?.orderStatusCounts);
          const cAll = this.normalizeCounts(blocks['all']?.orderStatusCounts);

          const pending = this.num(c24['pending']);
          const cancelled24h = this.num(c24['cancelled']);

          // Until you have a “current” status endpoint, we approximate "inProgress"
          // using all-time totals. If you later add “currentStatusCounts”, swap it in here.
          const inProgress =
            this.num(cAll['confirmed']) +
            this.num(cAll['cooking']) +
            this.num(cAll['delivery']);

          return {
            timezone,
            totals,
            statusPanels,
            attention: { pending, inProgress, cancelled24h },
          };
        }),
      );
  }

  // ----------------- helpers -----------------

  private mapStatusPanel(
    rangeLabel: string,
    block?: OrderStatusCountBlock,
  ): StatusPanel {
    const counts = this.normalizeCounts(block?.orderStatusCounts);

    const rows: StatusRow[] = Object.entries(counts).map(([key, count]) => ({
      key,
      label: this.prettyStatus(key),
      count: this.num(count),
      percent: 0,
      kind: this.kind(key),
    }));

    const total = rows.reduce((sum, r) => sum + r.count, 0);

    rows.forEach((r) => (r.percent = total > 0 ? (r.count / total) * 100 : 0));

    rows.sort((a, b) => {
      const prio = (k: StatusKind) =>
        k === 'pending'
          ? 0
          : k === 'confirmed'
            ? 1
            : k === 'cooking'
              ? 2
              : k === 'delivery'
                ? 3
                : k === 'completed'
                  ? 4
                  : k === 'cancelled'
                    ? 5
                    : 6;

      const p = prio(a.kind) - prio(b.kind);
      if (p !== 0) return p;
      return b.count - a.count;
    });

    return {
      rangeLabel,
      rangeStart: block?.range?.start ?? 'NotSet',
      rangeEnd: block?.range?.end ?? 'NotSet',
      total,
      rows,
    };
  }

  private normalizeCounts(raw?: OrderStatusCounts): Record<string, number> {
    if (!raw) return {};

    if (Array.isArray(raw)) {
      const out: Record<string, number> = {};
      for (const item of raw as OrderStatusCountsArrayItem[]) {
        if (!item || typeof item.status !== 'string') continue;
        out[item.status] = this.num(item.count);
      }
      return out;
    }

    if (typeof raw === 'object') {
      const out: Record<string, number> = {};
      for (const [k, v] of Object.entries(raw as Record<string, number>)) {
        out[k] = this.num(v);
      }
      return out;
    }

    return {};
  }

  private prettyStatus(key: string): string {
    const map: Record<string, string> = {
      pending: 'Pending',
      confirmed: 'Confirmed',
      cooking: 'Cooking',
      delivery: 'Out for delivery',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };

    return map[key] ?? this.titleCase(key.replace(/[_-]+/g, ' '));
  }

  private kind(key: string): StatusKind {
    const known = [
      'pending',
      'confirmed',
      'cooking',
      'delivery',
      'completed',
      'cancelled',
    ] as const;
    return (known as readonly string[]).includes(key)
      ? (key as StatusKind)
      : 'other';
  }

  private titleCase(s: string): string {
    return s
      .split(' ')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  private money(v: string): string {
    const n = Number(v);
    if (Number.isNaN(n)) return '0.00';
    return n.toFixed(2);
  }

  private num(v: any): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
}
